/**
 * Composite day state — determines a habit's status for a given date.
 *
 * Combines schedule, completion, and grace logic into a single enum.
 */

import { compareDates } from "./day-boundary";
import { isGraceDay } from "./grace";
import { isDue } from "./schedule";

import type { CompletionRecord, ScheduleConfig, ScheduleContext } from "./types";

export type HabitDayState = "due" | "done" | "missed" | "skipped" | "grace" | "not-scheduled";

/**
 * Determine the state of a habit on a given date.
 *
 * @param config - Parsed schedule config
 * @param completions - All completion records for this habit (used for grace/schedule checks)
 * @param date - The date to evaluate (YYYY-MM-DD)
 * @param context - Schedule context (habit start date, completions for every_n_days)
 * @param today - The current effective date (YYYY-MM-DD)
 * @param dueDates - Pre-computed due dates for the current week (needed by grace logic)
 * @param firstDayOfWeek - 0=Sun..6=Sat
 */
export function getHabitDayState(
  config: ScheduleConfig,
  completions: CompletionRecord[],
  date: string,
  context: ScheduleContext,
  today: string,
  dueDates: string[],
  firstDayOfWeek: number,
): HabitDayState {
  // 1. Not scheduled
  if (!isDue(config, date, context)) {
    return "not-scheduled";
  }

  // 2. Check completions for this date
  for (const c of completions) {
    if (c.effective_date !== date) continue;
    if (c.type === "done" || c.type === "backfill") return "done";
    if (c.type === "skip") return "skipped";
  }

  // 3. If date is in the past: grace or missed
  if (compareDates(date, today) < 0) {
    if (isGraceDay(completions, dueDates, date, firstDayOfWeek)) {
      return "grace";
    }
    return "missed";
  }

  // 4. Today (or future) with no completion
  return "due";
}
